'use client'
import { useState } from 'react'
import { Loader2, AlertCircle, Check, X, Sparkles, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { NewsletterCapture } from '@/components/marketing/newsletter-capture'
import { ToolProgress } from '@/components/tools/tool-progress'
import { TurnstileWidget, useTurnstileSiteKey } from '@/lib/turnstile'

type Row = { id: string; label: string; passed: boolean; evidence: string }
type Audit = { url: string; finalUrl: string; title: string; score: number; outOf: number; rows: Row[]; fetchedAt: string }
type Report = { audit: Audit; summary: string | null; aiEnabled: boolean }

// Decode HTML entities (e.g. &amp;, &ndash;) that come through in a fetched
// page <title>. textarea.innerHTML→value is safe and decodes everything.
function decodeHtml(s: string): string {
  if (!s) return s
  if (typeof document === 'undefined') return s.replace(/&amp;/g, '&').replace(/&ndash;/g, '–').replace(/&mdash;/g, '—')
  const el = document.createElement('textarea')
  el.innerHTML = s
  return el.value
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function AeoBrandReport() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [domain, setDomain] = useState('')
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<Report | null>(null)
  const [error, setError] = useState<string | null>(null)
  const turnstileSiteKey = useTurnstileSiteKey()
  const [turnstileToken, setTurnstileToken] = useState('')

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return setError('Please enter your name.')
    if (!EMAIL_RE.test(email.trim())) return setError('Please enter a valid work email.')
    if (!domain.trim()) return setError('Please enter your brand domain.')

    setLoading(true)
    setError(null)
    setData(null)

    // Capture the lead (fire-and-forget — never blocks the report).
    void fetch('/api/leads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email.trim(),
        name: name.trim(),
        company: domain.trim(),
        intent: 'try-tool',
        source: 'tool:aeo-brand-report',
        toolSlug: 'aeo-brand-report',
        'cf-turnstile-response': turnstileToken,
      }),
    }).catch(() => {})

    try {
      const res = await fetch('/api/tools/aeo-brand-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: domain.trim() }),
      })
      const json = await res.json()
      if (!res.ok) setError(json?.error || 'Report failed. Try a different domain.')
      else setData(json as Report)
    } catch {
      setError('Network error. Try again.')
    } finally {
      setLoading(false)
    }
  }

  const pct = data ? Math.round((data.audit.score / data.audit.outOf) * 100) : 0
  const band = pct >= 75 ? 'text-emerald-700 dark:text-emerald-300' : pct >= 50 ? 'text-amber-700 dark:text-amber-300' : 'text-rose-700 dark:text-rose-300'

  return (
    <div className="w-full max-w-3xl">
      <form onSubmit={onSubmit} className="grid gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name *"
            className="h-12 text-base"
            aria-label="Your name"
            autoComplete="name"
            required
          />
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            placeholder="Work email *"
            className="h-12 text-base"
            aria-label="Work email"
            autoComplete="email"
            required
          />
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <Input
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="Your brand domain (e.g. yourbrand.com) *"
            className="h-12 flex-1 text-base"
            aria-label="Brand domain"
            required
          />
          <Button type="submit" size="lg" disabled={loading} className="h-12 px-6">
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Auditing</> : <>Run report <Search className="ml-2 h-4 w-4" /></>}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          We email your report and relevant follow-ups. No drip sequence, unsubscribe anytime.
        </p>
        {turnstileSiteKey && (
          <TurnstileWidget size="compact" onToken={setTurnstileToken} onExpire={() => setTurnstileToken('')} />
        )}
      </form>

      {loading && (
        <ToolProgress
          label={domain.trim().replace(/^https?:\/\//, '')}
          steps={[
            { title: 'Finding your domain', sub: 'Fetching your site, robots.txt and llms.txt' },
            { title: 'Grading citability signals', sub: 'Schema, ratings, AI-bot access, llms.txt, Open Graph' },
            { title: 'Generating your report', sub: 'Scoring AI readiness, 0-100, with per-signal evidence' },
          ]}
        />
      )}

      {error && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {data && (
        <div className="mt-8 space-y-5">
          <div className="rounded-2xl bg-muted/40 p-6 sm:p-8">
            <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">AI citability score</p>
                <p className="mt-1 truncate font-display text-base font-medium" title={decodeHtml(data.audit.finalUrl)}>
                  {decodeHtml(data.audit.title) || data.audit.finalUrl}
                </p>
                <p className="mt-1 truncate text-xs text-muted-foreground">{data.audit.finalUrl}</p>
              </div>
              <div className="flex shrink-0 items-baseline gap-2">
                <span className={`font-display text-6xl font-bold tabular-nums ${band}`}>{data.audit.score}</span>
                <span className="text-2xl text-muted-foreground">/ {data.audit.outOf}</span>
              </div>
            </div>
          </div>

          {data.aiEnabled && data.summary && (
            <div className="rounded-2xl bg-gradient-to-br from-primary/[0.08] to-muted/40 p-5 sm:p-6">
              <p className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.22em] text-primary">
                <Sparkles className="h-3.5 w-3.5" /> AI summary
              </p>
              <p className="mt-3 whitespace-pre-wrap text-[15px] leading-relaxed text-foreground/90">{data.summary}</p>
            </div>
          )}

          <div>
            <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
              Citability signals
            </p>
            <ul className="mt-3 grid gap-2">
              {data.audit.rows.map((r) => (
                <li key={r.id} className="rounded-xl bg-muted/40 p-4">
                  <div className="flex items-start gap-3">
                    {r.passed ? (
                      <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"><Check className="h-3 w-3" /></span>
                    ) : (
                      <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-500/15 text-rose-700 dark:text-rose-300"><X className="h-3 w-3" /></span>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-medium leading-snug">{r.label}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{decodeHtml(r.evidence)}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <p className="text-xs leading-relaxed text-muted-foreground">
            This report grades the signals AI engines weight when deciding whether to cite a brand. It does not query ChatGPT, Claude, Perplexity or Gemini directly. Pass these signals and the citation rate follows.
            {!data.aiEnabled && ' The AI-written summary is off on this deployment; the deterministic signal grade above is the full report.'}
          </p>

          <div className="space-y-3 pt-2">
            <NewsletterCapture
              source="tool:aeo-brand-report"
              headline="Get the AI-citability checklist + new teardowns"
              caption="One email with the full AEO signal checklist this report runs on, plus new long-form teardowns when they ship. No drip sequence."
              cta="Send it over"
            />
            <p className="text-center text-xs text-muted-foreground">
              Idukki ships the schema, llms.txt and structured reviews that make a brand citable.{' '}
              <a href="https://app.idukki.io/register" className="font-medium text-primary underline underline-offset-4">Start a free trial</a>.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
